import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Tooltip as RTooltip,
  Legend,
} from "recharts";
import { Activity, Compass, RotateCw, Navigation2 } from "lucide-react";

const TICK_STYLE = { fontSize: 10 };
const GRID_STROKE = "rgba(255,255,255,0.05)";
const AXIS_STROKE = "#52525b";
const TOOLTIP_CONTENT_STYLE = {
  background: "#18181b",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: "#a1a1aa" };
const LEGEND_STYLE = { fontSize: 11, paddingTop: 4 };

function ChartCard({ title, subtitle, icon: Icon, color, children, testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-900 border border-white/10 p-5 rounded-xl">
      <div className="flex items-baseline justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <span className="text-sm font-heading font-semibold text-white">{title}</span>
        </div>
        {subtitle && <span className="text-[10px] font-mono text-zinc-500">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function TurnAreas({ turns }) {
  return (turns || []).map((t) => (
    <ReferenceArea
      key={`turn-${t.order}`}
      x1={t.startT}
      x2={t.endT === t.startT ? t.endT + 0.01 : t.endT}
      fill={t.ok ? "#10b981" : "#ef4444"}
      fillOpacity={0.12}
      stroke={t.ok ? "#10b981" : "#ef4444"}
      strokeOpacity={0.3}
      label={{ value: `${t.order}${t.direction === "LEFT" ? "L" : "R"}`, position: "insideTop", fill: "#a1a1aa", fontSize: 10 }}
    />
  ));
}

function BaseChart({ data, children, unit, turns }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid stroke={GRID_STROKE} />
        <XAxis
          dataKey="t"
          type="number"
          domain={["dataMin", "dataMax"]}
          stroke={AXIS_STROKE}
          tick={TICK_STYLE}
          tickFormatter={(v) => `${Number(v).toFixed(0)}s`}
        />
        <YAxis stroke={AXIS_STROKE} tick={TICK_STYLE} />
        <RTooltip
          contentStyle={TOOLTIP_CONTENT_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          labelFormatter={(v) => `t = ${Number(v).toFixed(2)} s`}
          formatter={(v, n) => [`${Number(v).toFixed(3)} ${unit}`, n]}
        />
        <Legend wrapperStyle={LEGEND_STYLE} />
        {turns && <TurnAreas turns={turns} />}
        {children}
      </LineChart>
    </ResponsiveContainer>
  );
}

/** Real sensor charts from the normalised series returned by getTestDetail. */
export function SensorCharts({ series, turns, threshold, method }) {
  if (!series?.length) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <ChartCard title="Accelerometer" subtitle="g · deviation from rest" icon={Activity} color="text-yellow-500" testId="chart-accelerometer">
        <BaseChart data={series} unit="g" turns={method === "accelX" ? turns : null}>
          <Line type="monotone" dataKey="ax" stroke="#eab308" strokeWidth={2} dot={false} name="X" isAnimationActive={false} />
          <Line type="monotone" dataKey="ay" stroke="#10b981" strokeWidth={1.5} dot={false} name="Y" isAnimationActive={false} />
          <Line type="monotone" dataKey="az" stroke="#ef4444" strokeWidth={1.5} dot={false} name="Z" isAnimationActive={false} />
          <Line type="monotone" dataKey="aMag" stroke="#a1a1aa" strokeDasharray="4 3" strokeWidth={1} dot={false} name="|a|" isAnimationActive={false} />
        </BaseChart>
      </ChartCard>

      <ChartCard title="Gyroscope" subtitle="deg/s · turns highlighted" icon={RotateCw} color="text-sky-400" testId="chart-gyroscope">
        <BaseChart data={series} unit="°/s" turns={method !== "accelX" ? turns : null}>
          {threshold && method === "gyroZ" && (
            <>
              <ReferenceLine y={threshold} stroke="#52525b" strokeDasharray="3 3" />
              <ReferenceLine y={-threshold} stroke="#52525b" strokeDasharray="3 3" />
            </>
          )}
          <Line type="monotone" dataKey="gz" stroke="#38bdf8" strokeWidth={2} dot={false} name="Z (yaw)" isAnimationActive={false} />
          <Line type="monotone" dataKey="gx" stroke="#f97316" strokeWidth={1.2} dot={false} name="X (roll)" isAnimationActive={false} />
          <Line type="monotone" dataKey="gy" stroke="#a78bfa" strokeWidth={1.2} dot={false} name="Y (pitch)" isAnimationActive={false} />
        </BaseChart>
      </ChartCard>

      <ChartCard title="Magnetometer" subtitle="µT" icon={Compass} color="text-emerald-400" testId="chart-magnetometer">
        <BaseChart data={series} unit="µT">
          <Line type="monotone" dataKey="mx" stroke="#06b6d4" strokeWidth={2} dot={false} name="X" isAnimationActive={false} />
          <Line type="monotone" dataKey="my" stroke="#a78bfa" strokeWidth={1.5} dot={false} name="Y" isAnimationActive={false} />
          <Line type="monotone" dataKey="mz" stroke="#f97316" strokeWidth={1.5} dot={false} name="Z" isAnimationActive={false} />
        </BaseChart>
      </ChartCard>

      <ChartCard title="Heading" subtitle="deg · from magnetometer" icon={Navigation2} color="text-violet-400" testId="chart-heading">
        <BaseChart data={series} unit="°" turns={turns}>
          <Line type="monotone" dataKey="heading" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Heading" isAnimationActive={false} />
        </BaseChart>
      </ChartCard>
    </div>
  );
}
